/**
 * ReasoningTimeline — the agent's chain-of-thought, rendered as a vertical
 * timeline inside a "Reasoning" card (Besoin → Analyse → Critères → Recherche).
 * Steps appear as their SSE events arrive. No emojis, brand purple accent.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Cpu,
  FlaskConical,
  Wand2,
  ListChecks,
  Search,
  CheckCircle2,
  Loader2,
} from 'lucide-react';

import { Tag } from '../../atoms/Tag';
import type { AgentRunState } from './types';

const Step: React.FC<{
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
}> = ({ icon, title, children }) => (
  // Dot is centered on the rail: container has pl-7 (28px) so content starts at
  // 28px; the rail line sits at x=7px; the dot (10px wide) at -1.625rem (-26px)
  // → its left is 2px and its center is 7px, exactly on the line.
  <div className="relative pb-5 last:pb-0">
    <span className="absolute -left-[1.625rem] top-1 h-2.5 w-2.5 rounded-full bg-purple ring-4 ring-white" />
    <div className="flex items-center gap-2 text-sm font-semibold text-dark-invirtus">
      <span className="text-purple">{icon}</span>
      {title}
    </div>
    {children != null && <div className="mt-1.5 text-sm text-dark-invirtus/70">{children}</div>}
  </div>
);

export const ReasoningTimeline: React.FC<{ state: AgentRunState }> = ({ state }) => {
  const { t } = useTranslation('agents');
  const running = state.phase === 'running';

  return (
    <section className="rounded-2xl border border-light-lila p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-soft text-purple">
          <Cpu className="h-4 w-4" />
        </span>
        <span className="text-sm font-semibold text-dark-invirtus">
          {t('sourcing.reasoning')}
        </span>
      </div>

      <div className="relative ml-3 pl-7">
        {/* Vertical rail at x=7px; dots are centered on it. */}
        <span
          aria-hidden
          className="absolute left-[7px] top-1 bottom-2 w-px bg-light-lila"
        />
        {/* Besoin */}
        {state.need && (
          <Step icon={<FlaskConical/>} title={t('sourcing.step.need')}>
            <p className="text-dark-invirtus">{state.need.title}</p>
            {state.need.chips.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {state.need.chips.map((c, i) => (
                  <Tag
                    key={`${c.kind}-${i}`}
                    intent="secondary"
                    variant="subtle"
                  >
                    {c.label}
                  </Tag>
                ))}
              </div>
            )}
          </Step>
        )}

        {/* Analyse du besoin */}
        {(state.analysis || running) && (
          <Step icon={<Wand2/>} title={t('sourcing.step.analysis')}>
            {state.analysis ? (
              <p>{state.analysis}</p>
            ) : (
              <div className="flex items-center gap-2 rounded-xl bg-background-gray px-3 py-3 text-dark-invirtus/60">
                <Cpu className="h-4 w-4 animate-pulse" />
                {t('sourcing.analysing')}
              </div>
            )}
          </Step>
        )}

        {/* Critères de recherche */}
        {state.criteria && (
          <Step icon={<ListChecks/>} title={t('sourcing.step.criteria')}>
            <p>{state.criteria.text}</p>
            {state.criteria.items.length > 0 && (
              <ol className="mt-2 space-y-1.5">
                {state.criteria.items.map((it, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-dark-invirtus/50">{i + 1}.</span>
                    <span className="rounded-lg bg-background-gray px-2 py-1 text-dark-invirtus/80">
                      {it.text}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Step>
        )}

        {/* Recherche en cours — presented as sequential angles (one call underneath) */}
        {(state.searchReasoning || state.steps.length > 0) && (
          <Step icon={<Search/>} title={t('sourcing.step.search')}>
            {state.searchReasoning && <p>{state.searchReasoning}</p>}
            {state.steps.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {state.steps.map((s) => (
                  <div
                    key={s.index}
                    className="flex items-center gap-2 rounded-xl border border-light-lila px-3 py-2"
                  >
                    {s.status === 'done' ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-green" />
                    ) : (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-purple" />
                    )}
                    <span className="text-sm font-medium text-dark-invirtus">{s.label}</span>
                  </div>
                ))}
              </div>
            )}
            {state.resultCount != null && (
              <p className="mt-2 text-xs font-medium text-dark-invirtus/70">
                {t('sourcing.search_found', { count: state.resultCount })}
              </p>
            )}
            {import.meta.env.DEV && state.debug != null && (
              <details className="mt-2 rounded-xl border border-light-lila bg-background-gray p-2">
                <summary className="cursor-pointer text-xs font-semibold text-dark-invirtus/70">
                  {t('sourcing.debug_title')}
                </summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all text-[11px] text-dark-invirtus/80">
                  {JSON.stringify(state.debug.attempts, null, 2)}
                </pre>
              </details>
            )}
          </Step>
        )}

        {running && (
          <div className="flex items-center gap-2 py-1 text-xs text-dark-invirtus/50">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          </div>
        )}
      </div>
    </section>
  );
};

ReasoningTimeline.displayName = 'ReasoningTimeline';
